import type { SupabaseClient } from "@supabase/supabase-js"

import { normalizeIsoCurrency, resolveUserCurrency } from "@/lib/app-currency"
import {
  findVisaMetadataRowForRoute,
  VISA_METADATA_ENRICHMENT_LIVE,
  VISA_METADATA_VERIFICATION_VERIFIED,
} from "@/lib/visa-utils"

export type ResolveWorkspaceCurrencyInput = {
  userId: string
  /** When set, wins over profile / `visa_metadata` (user-chosen budget currency). */
  explicitBudgetCurrency?: string | null
  nationalityCode: string
  destinationCode: string
  /** Visa slug for a specific `visa_metadata` row; when empty, only the “any row” lookup runs. */
  visaTypeForMetadata: string
  /** Browser locale (e.g. `en-US`) for {@link resolveUserCurrency} when profile fields are sparse. */
  navigatorLocale?: string | null
}

/** Prefer live+verified rows so unverified metadata cannot skew budget currency. */
async function fetchFirstDestinationCurrencyForRoute(
  client: SupabaseClient,
  nationalityCode: string,
  destinationCode: string
): Promise<string | null> {
  const nat = nationalityCode.trim().toUpperCase()
  const dest = destinationCode.trim().toUpperCase()
  if (!nat || !dest) return null

  const { data, error } = await client
    .from("visa_metadata")
    .select("destination_currency")
    .eq("nationality_code", nat)
    .eq("destination_code", dest)
    .eq("enrichment_status", VISA_METADATA_ENRICHMENT_LIVE)
    .eq("verification_status", VISA_METADATA_VERIFICATION_VERIFIED)
    .not("destination_currency", "is", null)
    .limit(1)

  if (error || !data?.length) return null
  const row = data[0] as Record<string, unknown>
  const raw = row.destination_currency
  return typeof raw === "string" && raw.trim() ? raw.trim() : null
}

/**
 * Workspace budget ISO code:
 *
 * 1. Explicit `budgetCurrency` / `currency` from the caller (UI override).
 * 2. When `profiles.preferred_currency` is set: {@link resolveUserCurrency} (preferred wins; no visa override).
 * 3. When preferred is unset: `visa_metadata.destination_currency` for a live+verified route row
 *    (exact visa type, else any verified row for the pair) so destination fee context can apply.
 * 4. Otherwise {@link resolveUserCurrency} with profile + `navigatorLocale`: shared residency map
 *    (`src/lib/currency-utils.ts` → browser region from the same map → `USD`; BRL only for Brazil `BR`).
 */
export async function resolveWorkspaceCurrency(
  client: SupabaseClient,
  input: ResolveWorkspaceCurrencyInput
): Promise<string> {
  const explicit = normalizeIsoCurrency(input.explicitBudgetCurrency)
  if (explicit) return explicit

  const { data: prof, error: pErr } = await client
    .from("profiles")
    .select("preferred_currency, country_of_residence")
    .eq("id", input.userId)
    .maybeSingle()

  const profRow = !pErr && prof ? (prof as Record<string, unknown>) : null
  const slice = profRow
    ? {
        preferred_currency:
          typeof profRow.preferred_currency === "string" ? profRow.preferred_currency : null,
        country_of_residence:
          typeof profRow.country_of_residence === "string" ? profRow.country_of_residence : null,
      }
    : null
  const hadPreferred = Boolean(normalizeIsoCurrency(slice?.preferred_currency ?? null))

  const userResolved = resolveUserCurrency(slice, {
    navigatorLanguage: input.navigatorLocale ?? null,
  })

  if (hadPreferred) return userResolved

  const nat = input.nationalityCode.trim().toUpperCase()
  const dest = input.destinationCode.trim().toUpperCase()
  const vt = input.visaTypeForMetadata.trim()

  if (vt) {
    const { row } = await findVisaMetadataRowForRoute(client, nat, dest, vt)
    const dcRaw = row?.destination_currency
    const fromRow =
      typeof dcRaw === "string" && dcRaw.trim() ? normalizeIsoCurrency(dcRaw.trim()) : null
    if (fromRow) return fromRow
  }

  const anyDc = await fetchFirstDestinationCurrencyForRoute(client, nat, dest)
  const fromAny = anyDc ? normalizeIsoCurrency(anyDc) : null
  if (fromAny) return fromAny

  return userResolved
}
